import React from 'react';
import { XIcon, UserIcon, GlobeIcon, MapPinIcon, BuildingIcon, MailIcon, BriefcaseIcon, FileTextIcon, TrendingUpIcon, TrendingDownIcon, BarChart4Icon, DollarSignIcon, PieChartIcon, CheckCircleIcon, ClockIcon, XCircleIcon, ArrowUpRightIcon, PlusIcon, UsersIcon, CalendarIcon, StarIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
export function BusinessUnitUserModal({
  user,
  onClose
}) {
  if (!user) return null;
  // Datos de muestra para las cotizaciones del usuario
  const userQuoteStats = {
    total: 47,
    active: 18,
    pending: 12,
    approved: 10,
    expired: 7,
    totalValue: 1432500,
    winRate: 68,
    avgQuoteValue: 30479,
    changeFromLastMonth: 8.5
  };
  // Datos para el gráfico de tendencia del mercado
  const marketTrendData = [{
    month: 'Jan',
    value: 230000,
    prevValue: 210000
  }, {
    month: 'Feb',
    value: 280000,
    prevValue: 240000
  }, {
    month: 'Mar',
    value: 250000,
    prevValue: 270000
  }, {
    month: 'Apr',
    value: 310000,
    prevValue: 230000
  }, {
    month: 'May',
    value: 350000,
    prevValue: 290000
  }, {
    month: 'Jun',
    value: 320000,
    prevValue: 310000
  }, {
    month: 'Jul',
    value: 390000,
    prevValue: 320000
  }, {
    month: 'Aug',
    value: 420000,
    prevValue: 350000
  }];
  // Datos para el gráfico de distribución de cotizaciones
  const quoteDistributionData = [{
    name: 'Active',
    value: userQuoteStats.active,
    color: '#22c55e'
  }, {
    name: 'Pending',
    value: userQuoteStats.pending,
    color: '#eab308'
  }, {
    name: 'Approved',
    value: userQuoteStats.approved,
    color: '#3b82f6'
  }, {
    name: 'Expired',
    value: userQuoteStats.expired,
    color: '#ef4444'
  }];
  // Datos de distribuidores
  const dealersStats = {
    total: user.dealers.length,
    active: Math.floor(user.dealers.length * 0.85),
    newThisQuarter: 3,
    growthRate: 12.5,
    topPerformer: user.dealers[0],
    newDealers: [{
      name: 'AgriTech Solutions',
      location: user.countries[0],
      date: '2 months ago',
      sales: '$125,400'
    }, {
      name: 'Farm Innovations Ltd',
      location: user.countries[1],
      date: '3 months ago',
      sales: '$98,700'
    }, {
      name: 'Irrigation Experts',
      location: user.countries[2],
      date: '5 months ago',
      sales: '$76,300'
    }]
  };
  // Datos para el gráfico de rendimiento por país
  const countryPerformanceData = user.countries.map(country => ({
    country,
    value: Math.floor(Math.random() * 200000) + 50000,
    quota: Math.floor(Math.random() * 250000) + 100000
  })).sort((a, b) => b.value - a.value);
  return <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-3200">
      <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} data-id="element-3201">
        {/* Header */}
        <div className="p-5 border-b border-slate-700 flex justify-between items-center sticky top-0 bg-slate-800 z-10" data-id="element-3202">
          <h2 className="text-xl font-bold text-white flex items-center" data-id="element-3203">
            <UserIcon size={20} className="mr-2 text-indigo-400" data-id="element-3204" />
            Business Unit User Details
          </h2>
          <button className="text-slate-400 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded-full p-1.5" onClick={onClose} data-id="element-3205">
            <XIcon size={18} data-id="element-3206" />
          </button>
        </div>
        {/* User Info */}
        <div className="p-6" data-id="element-3207">
          <div className="flex flex-col md:flex-row md:items-start gap-4 mb-6" data-id="element-3208">
            {/* Left: Avatar + Identity */}
            <div className="flex items-center flex-shrink-0" data-id="element-3209">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl font-semibold mr-4 ring-2 ring-indigo-400/30" data-id="element-3210">
                {user.name.split(' ').map(name => name[0]).join('')}
              </div>
              <div data-id="element-3211">
                <h3 className="text-lg font-bold text-white leading-tight" data-id="element-3212">
                  {user.name}
                </h3>
                <div className="flex items-center text-slate-300 text-sm mt-0.5" data-id="element-3213">
                  <BriefcaseIcon size={13} className="mr-1.5 text-slate-400" data-id="element-3214" />
                  {user.role}
                </div>
                <div className="flex items-center text-slate-300 text-sm mt-0.5" data-id="element-3215">
                  <MailIcon size={13} className="mr-1.5 text-slate-400" data-id="element-3216" />
                  {user.email}
                </div>
              </div>
            </div>

            {/* Right: Business Unit + Coverage panels */}
            <div className="flex flex-col sm:flex-row gap-3 md:ml-auto" data-id="element-3217">
              {/* Assigned Business Unit */}
              <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600/50 min-w-[180px]" data-id="element-3218">
                <div className="flex items-center justify-between mb-1.5" data-id="element-3219">
                  <span className="text-xs text-slate-400" data-id="element-3220">
                    Assigned Business Unit
                  </span>
                  <GlobeIcon size={14} className="text-indigo-400" data-id="element-3221" />
                </div>
                <div className="text-base font-bold text-white leading-tight" data-id="element-3222">
                  {user.businessUnit}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-3223">
                  {user.regions.map(region => <span key={region} className="bg-indigo-900/30 text-indigo-300 border border-indigo-800/30 px-2 py-0.5 rounded text-[11px]" data-id="element-3224">
                      {region}
                    </span>)}
                </div>
              </div>

              {/* Coverage */}
              <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600/50 min-w-[180px]" data-id="element-3225">
                <div className="flex items-center justify-between mb-1.5" data-id="element-3226">
                  <span className="text-xs text-slate-400" data-id="element-3227">Coverage</span>
                  <MapPinIcon size={14} className="text-amber-400" data-id="element-3228" />
                </div>
                <div className="flex items-center gap-4 mb-2" data-id="element-3229">
                  <div data-id="element-3230">
                    <div className="text-base font-bold text-white" data-id="element-3231">
                      {user.countries.length}
                    </div>
                    <div className="text-[11px] text-slate-400" data-id="element-3232">Countries</div>
                  </div>
                  <div className="w-px h-8 bg-slate-600" data-id="element-3233"></div>
                  <div data-id="element-3234">
                    <div className="text-base font-bold text-white" data-id="element-3235">
                      {user.dealers.length}
                    </div>
                    <div className="text-[11px] text-slate-400" data-id="element-3236">Dealers</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5" data-id="element-3237">
                  {user.countries.slice(0, 3).map(country => <span key={country} className="bg-amber-900/30 text-amber-300 border border-amber-800/30 px-2 py-0.5 rounded text-[11px]" data-id="element-3238">
                      {country}
                    </span>)}
                  {user.countries.length > 3 && <span className="bg-slate-700 text-slate-300 border border-slate-600 px-2 py-0.5 rounded text-[11px]" data-id="element-3239">
                      +{user.countries.length - 3} more
                    </span>}
                </div>
              </div>
            </div>
          </div>

          {/* KPI Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-id="element-3240">
            <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600/50" data-id="element-3241">
              <div className="flex items-center justify-between mb-1" data-id="element-3242">
                <span className="text-xs text-slate-400" data-id="element-3243">Total Quotes</span>
                <FileTextIcon size={14} className="text-blue-400" data-id="element-3244" />
              </div>
              <div className="text-xl font-bold text-white" data-id="element-3245">
                {userQuoteStats.total}
              </div>
              <div className="text-xs text-green-400 flex items-center mt-1" data-id="element-3246">
                <TrendingUpIcon size={12} className="mr-1" data-id="element-3247" />
                {userQuoteStats.changeFromLastMonth}% from last month
              </div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600/50" data-id="element-3248">
              <div className="flex items-center justify-between mb-1" data-id="element-3249">
                <span className="text-xs text-slate-400" data-id="element-3250">Win Rate</span>
                <CheckCircleIcon size={14} className="text-green-400" data-id="element-3251" />
              </div>
              <div className="text-xl font-bold text-white" data-id="element-3252">
                {userQuoteStats.winRate}%
              </div>
              <div className="text-xs text-green-400 flex items-center mt-1" data-id="element-3253">
                <ArrowUpRightIcon size={12} className="mr-1" data-id="element-3254" />
                3.2% from last quarter
              </div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600/50" data-id="element-3255">
              <div className="flex items-center justify-between mb-1" data-id="element-3256">
                <span className="text-xs text-slate-400" data-id="element-3257">Total Value</span>
                <DollarSignIcon size={14} className="text-emerald-400" data-id="element-3258" />
              </div>
              <div className="text-xl font-bold text-white" data-id="element-3259">
                ${(userQuoteStats.totalValue / 1000).toFixed(0)}k
              </div>
              <div className="text-xs text-slate-400 mt-1" data-id="element-3260">
                Avg: ${userQuoteStats.avgQuoteValue.toLocaleString()}
              </div>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600/50" data-id="element-3261">
              <div className="flex items-center justify-between mb-1" data-id="element-3262">
                <span className="text-xs text-slate-400" data-id="element-3263">Active Quotes</span>
                <ClockIcon size={14} className="text-amber-400" data-id="element-3264" />
              </div>
              <div className="text-xl font-bold text-white" data-id="element-3265">
                {userQuoteStats.active}
              </div>
              <div className="text-xs text-amber-400 flex items-center mt-1" data-id="element-3266">
                <ClockIcon size={12} className="mr-1" data-id="element-3267" />
                {userQuoteStats.pending} pending review
              </div>
            </div>
          </div>

          {/* Quote Statistics */}
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600/50 mb-6" data-id="element-3268">
            <h4 className="text-white font-medium flex items-center mb-4" data-id="element-3269">
              <FileTextIcon size={16} className="mr-2 text-blue-400" data-id="element-3270" />
              Quote Statistics
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-3271">
              <div className="bg-slate-800/70 rounded-lg p-3" data-id="element-3272">
                <div className="text-xs text-slate-400 mb-1" data-id="element-3273">Active Quotes</div>
                <div className="text-xl font-bold text-white flex items-center" data-id="element-3274">
                  {userQuoteStats.active}
                  <span className="ml-2 text-xs px-1.5 py-0.5 bg-green-900/40 text-green-400 rounded-md" data-id="element-3275">
                    {Math.round(userQuoteStats.active / userQuoteStats.total * 100)}
                    %
                  </span>
                </div>
              </div>
              <div className="bg-slate-800/70 rounded-lg p-3" data-id="element-3276">
                <div className="text-xs text-slate-400 mb-1" data-id="element-3277">Pending</div>
                <div className="text-xl font-bold text-white flex items-center" data-id="element-3278">
                  {userQuoteStats.pending}
                  <span className="ml-2 text-xs px-1.5 py-0.5 bg-amber-900/40 text-amber-400 rounded-md" data-id="element-3279">
                    {Math.round(userQuoteStats.pending / userQuoteStats.total * 100)}
                    %
                  </span>
                </div>
              </div>
              <div className="bg-slate-800/70 rounded-lg p-3" data-id="element-3280">
                <div className="text-xs text-slate-400 mb-1" data-id="element-3281">Approved</div>
                <div className="text-xl font-bold text-white flex items-center" data-id="element-3282">
                  {userQuoteStats.approved}
                  <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-900/40 text-blue-400 rounded-md" data-id="element-3283">
                    {Math.round(userQuoteStats.approved / userQuoteStats.total * 100)}
                    %
                  </span>
                </div>
              </div>
              <div className="bg-slate-800/70 rounded-lg p-3" data-id="element-3284">
                <div className="text-xs text-slate-400 mb-1" data-id="element-3285">Expired</div>
                <div className="text-xl font-bold text-white flex items-center" data-id="element-3286">
                  {userQuoteStats.expired}
                  <span className="ml-2 text-xs px-1.5 py-0.5 bg-red-900/40 text-red-400 rounded-md" data-id="element-3287">
                    {Math.round(userQuoteStats.expired / userQuoteStats.total * 100)}
                    %
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3288">
              <div data-id="element-3289">
                <h5 className="text-sm font-medium text-slate-300 mb-3" data-id="element-3290">
                  Quote Distribution
                </h5>
                <div className="h-[200px]" data-id="element-3291">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-3292">
                    <PieChart data-id="element-3293">
                      <Pie data={quoteDistributionData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-3294">
                        {quoteDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-3295" />)}
                      </Pie>
                      <Tooltip contentStyle={{
                      backgroundColor: '#1e293b',
                      borderColor: '#475569',
                      borderRadius: '0.5rem'
                    }} itemStyle={{
                      color: '#e2e8f0'
                    }} formatter={value => [`${value} quotes`, '']} labelFormatter={() => ''} data-id="element-3296" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-2" data-id="element-3297">
                  {quoteDistributionData.map(entry => <div key={entry.name} className="flex items-center text-xs" data-id="element-3298">
                      <span className="w-3 h-3 rounded-full mr-1" style={{
                    backgroundColor: entry.color
                  }} data-id="element-3299"></span>
                      <span className="text-slate-300" data-id="element-3300">{entry.name}</span>
                    </div>)}
                </div>
              </div>
              <div data-id="element-3301">
                <h5 className="text-sm font-medium text-slate-300 mb-3" data-id="element-3302">
                  Country Performance
                </h5>
                <div className="h-[200px]" data-id="element-3303">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-3304">
                    <BarChart data={countryPerformanceData.slice(0, 5)} layout="vertical" margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5
                  }} data-id="element-3305">
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#475569" data-id="element-3306" />
                      <XAxis type="number" stroke="#94a3b8" fontSize={10} data-id="element-3307" />
                      <YAxis dataKey="country" type="category" scale="band" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} width={60} data-id="element-3308" />
                      <Tooltip contentStyle={{
                      backgroundColor: '#1e293b',
                      borderColor: '#475569',
                      borderRadius: '0.5rem'
                    }} itemStyle={{
                      color: '#e2e8f0'
                    }} formatter={value => [`$${value.toLocaleString()}`, 'Sales']} data-id="element-3309" />
                      <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} data-id="element-3310" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
          {/* Market Trend */}
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600/50 mb-6" data-id="element-3311">
            <h4 className="text-white font-medium flex items-center mb-4" data-id="element-3312">
              <TrendingUpIcon size={16} className="mr-2 text-green-400" data-id="element-3313" />
              Market Trend
            </h4>
            <div className="h-[250px]" data-id="element-3314">
              <ResponsiveContainer width="100%" height="100%" data-id="element-3315">
                <LineChart data={marketTrendData} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 20
              }} data-id="element-3316">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#475569" data-id="element-3317" />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={10} axisLine={false} tickLine={false} padding={{
                  left: 10,
                  right: 10
                }} data-id="element-3318" />
                  <YAxis stroke="#94a3b8" fontSize={10} axisLine={false} tickLine={false} tickFormatter={value => `$${value / 1000}k`} data-id="element-3319" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#1e293b',
                  borderColor: '#475569',
                  borderRadius: '0.5rem'
                }} itemStyle={{
                  color: '#e2e8f0'
                }} formatter={value => [`$${value.toLocaleString()}`, 'Sales']} data-id="element-3320" />
                  <Line type="monotone" dataKey="prevValue" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Previous Year" data-id="element-3321" />
                  <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={3} dot={{
                  r: 4,
                  fill: '#3b82f6',
                  strokeWidth: 0
                }} activeDot={{
                  r: 6,
                  fill: '#3b82f6',
                  stroke: '#fff',
                  strokeWidth: 2
                }} name="Current Year" data-id="element-3322" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-2 text-xs" data-id="element-3323">
              <div className="flex items-center" data-id="element-3324">
                <span className="w-3 h-1 bg-blue-500 mr-1 rounded-full" data-id="element-3325"></span>
                <span className="text-slate-300" data-id="element-3326">Current Year</span>
              </div>
              <div className="flex items-center" data-id="element-3327">
                <span className="w-3 h-1 bg-slate-400 mr-1 rounded-full" data-id="element-3328"></span>
                <span className="text-slate-300" data-id="element-3329">Previous Year</span>
              </div>
            </div>
          </div>
          {/* Dealers */}
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600/50 mb-6" data-id="element-3330">
            <div className="flex justify-between items-center mb-4" data-id="element-3331">
              <h4 className="text-white font-medium flex items-center" data-id="element-3332">
                <BuildingIcon size={16} className="mr-2 text-indigo-400" data-id="element-3333" />
                Dealers
              </h4>
              <div className="flex items-center bg-indigo-900/40 text-indigo-300 border border-indigo-800/30 px-3 py-1 rounded-lg text-sm" data-id="element-3334">
                <UsersIcon size={14} className="mr-1.5" data-id="element-3335" />
                {dealersStats.total} Total Dealers
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-3336">
              <div className="bg-slate-800/70 rounded-lg p-3" data-id="element-3337">
                <div className="text-xs text-slate-400 mb-1" data-id="element-3338">
                  Active Dealers
                </div>
                <div className="text-xl font-bold text-white" data-id="element-3339">
                  {dealersStats.active}
                </div>
                <div className="text-xs text-green-400 flex items-center mt-1" data-id="element-3340">
                  <CheckCircleIcon size={12} className="mr-1" data-id="element-3341" />
                  {Math.round(dealersStats.active / dealersStats.total * 100)}
                  % active rate
                </div>
              </div>
              <div className="bg-slate-800/70 rounded-lg p-3" data-id="element-3342">
                <div className="text-xs text-slate-400 mb-1" data-id="element-3343">
                  New This Quarter
                </div>
                <div className="text-xl font-bold text-white" data-id="element-3344">
                  {dealersStats.newThisQuarter}
                </div>
                <div className="text-xs text-blue-400 flex items-center mt-1" data-id="element-3345">
                  <PlusIcon size={12} className="mr-1" data-id="element-3346" />
                  {dealersStats.growthRate}% growth rate
                </div>
              </div>
              <div className="bg-slate-800/70 rounded-lg p-3" data-id="element-3347">
                <div className="text-xs text-slate-400 mb-1" data-id="element-3348">Top Performer</div>
                <div className="text-base font-bold text-white truncate" data-id="element-3349">
                  {dealersStats.topPerformer}
                </div>
                <div className="text-xs text-amber-400 flex items-center mt-1" data-id="element-3350">
                  <StarIcon size={12} className="mr-1" data-id="element-3351" />
                  4.9 rating
                </div>
              </div>
            </div>
            <div data-id="element-3352">
              <h5 className="text-sm font-medium text-slate-300 mb-3 flex items-center" data-id="element-3353">
                <PlusIcon size={14} className="mr-1.5 text-green-400" data-id="element-3354" />
                Recently Added Dealers
              </h5>
              <div className="overflow-hidden rounded-lg border border-slate-700" data-id="element-3355">
                <table className="min-w-full divide-y divide-slate-700" data-id="element-3356">
                  <thead className="bg-slate-800/70" data-id="element-3357">
                    <tr data-id="element-3358">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-3359">
                        Dealer
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-3360">
                        Location
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-3361">
                        Added
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-3362">
                        Initial Sales
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-slate-800/50 divide-y divide-slate-700" data-id="element-3363">
                    {dealersStats.newDealers.map((dealer, index) => <tr key={index} className="hover:bg-slate-700/30" data-id="element-3364">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white" data-id="element-3365">
                          {dealer.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300" data-id="element-3366">
                          {dealer.location}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300" data-id="element-3367">
                          <div className="flex items-center" data-id="element-3368">
                            <CalendarIcon size={12} className="mr-1.5 text-slate-400" data-id="element-3369" />
                            {dealer.date}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-green-400 font-medium" data-id="element-3370">
                          {dealer.sales}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="p-5 border-t border-slate-700 flex justify-between" data-id="element-3371">
          <div className="text-sm text-slate-400" data-id="element-3372">
            Last updated:{' '}
            <span className="text-slate-300" data-id="element-3373">Yesterday at 4:30 PM</span>
          </div>
          <button className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg transition-colors" onClick={onClose} data-id="element-3374">
            Close
          </button>
        </div>
      </div>
    </div>;
}